/**
 * SortableOption Component
 * Draggable option item for question cards
 */

import React from 'react';
import { Tooltip } from 'antd';
import { InfoCircleOutlined, HolderOutlined } from '@ant-design/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IFlowOption } from '../../utils/jsonFlowGenerator';

interface SortableOptionProps {
  option: IFlowOption;
  formatPrice: (price?: number, priceModifier?: number) => string;
}

export const SortableOption: React.FC<SortableOptionProps> = ({ option, formatPrice }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.optionId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div className="flex items-start justify-between py-1.5 px-2 bg-white rounded border border-gray-100">
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 mr-1 text-gray-400 hover:text-gray-600 shrink-0"
        >
          <HolderOutlined style={{ fontSize: 12 }} />
        </div>

        {/* Option Image */}
        {option.thumbnail && (
          <div className="w-12 h-12 mr-2 shrink-0 rounded overflow-hidden bg-gray-50">
            <img
              src={option.thumbnail}
              alt={option.optionText}
              className="w-full h-full object-cover"
              onError={(e) => {
                // Hide image on error
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="font-medium text-xs text-gray-800">{option.optionText}</div>
          {option.description && (
            <div className="text-xs text-gray-400 truncate">{option.description}</div>
          )}
        </div>
        <div className="flex items-center gap-1 ml-2 shrink-0">
          <span
            className={`text-xs font-semibold ${
              (option.price ?? option.priceModifier ?? 0) > 0
                ? 'text-red-500'
                : 'text-gray-400'
            }`}
          >
            {formatPrice(option.price, option.priceModifier)}
          </span>
          {option.displayCondition?.enabled && (
            <Tooltip title="Has display conditions">
              <InfoCircleOutlined className="text-blue-500 text-xs" />
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};
